// Slice D4 — <BrandCombobox /> canonical-fed combobox for make/model/engine-mfr.
//
// Popover + cmdk Command. We do our OWN substring filtering (shouldFilter=false)
// so the rendered option set is deterministic (and testable) and so the
// free-text "Use …" create row stays visible regardless of cmdk's matcher.
// Open vocabulary: a value not in `options` is committed verbatim via the
// create row — the live D3 save path canonicalizes it server-side.
//
// Controlled + presentational: value/onChange only, no data fetching. The
// "Did you mean?" chip is driven by the `suggestion` prop and rendered OUTSIDE
// the popover so it is visible on the closed control.

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button, ButtonArrow } from '@marine/ui/button';
import {
  Command,
  CommandCheck,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@marine/ui/popover';
import { ScrollArea } from '@marine/ui/scroll-area';

export interface BrandComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Mid-band canonical near-match → "Did you mean X?" chip. */
  suggestion?: string | null;
  onAcceptSuggestion?: (name: string) => void;
}

export function BrandCombobox({
  value,
  onChange,
  options,
  placeholder = 'Select or type…',
  searchPlaceholder = 'Search or add…',
  loading = false,
  disabled = false,
  id,
  className,
  suggestion,
  onAcceptSuggestion,
}: BrandComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const commit = (v: string) => {
    onChange(v);
    setOpen(false);
    setSearch('');
  };

  const trimmed = search.trim();
  const filtered =
    trimmed === ''
      ? options
      : options.filter((o) => o.toLowerCase().includes(trimmed.toLowerCase()));
  const hasExact = options.some((o) => o.toLowerCase() === trimmed.toLowerCase());
  const showCreate = trimmed.length > 0 && !hasExact;

  // Hide the chip once the value already equals the suggested canonical name.
  const showSuggestion =
    !!suggestion && suggestion.toLowerCase() !== value.trim().toLowerCase();

  return (
    <div className={cn('space-y-1', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            mode="input"
            placeholder={!value}
            aria-expanded={open}
            disabled={disabled || loading}
            className="w-full justify-between"
          >
            {value ? (
              <span className="truncate">{value}</span>
            ) : (
              <span className="text-muted-foreground">
                {loading ? 'Loading…' : placeholder}
              </span>
            )}
            <ButtonArrow />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <ScrollArea viewportClassName="max-h-[260px] [&>div]:block!">
                <CommandEmpty>No matches.</CommandEmpty>
                <CommandGroup>
                  {showCreate && (
                    <CommandItem
                      value={`__create__${trimmed}`}
                      onSelect={() => commit(trimmed)}
                    >
                      Use "{trimmed}"
                    </CommandItem>
                  )}
                  {filtered.map((opt) => (
                    <CommandItem key={opt} value={opt} onSelect={() => commit(opt)}>
                      <span className="truncate">{opt}</span>
                      <CommandCheck
                        className={cn(value === opt ? 'opacity-100' : 'opacity-0')}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showSuggestion && (
        <button
          type="button"
          onClick={() => onAcceptSuggestion?.(suggestion as string)}
          className="text-xs text-muted-foreground hover:text-foreground text-left"
        >
          Did you mean{' '}
          <span className="font-semibold text-foreground underline">{suggestion}</span>?
        </button>
      )}
    </div>
  );
}
